// WCAG 2.x relative-luminance contrast math plus the branding page's checks.
// Contrast ratio thresholds: 4.5:1 for normal text (AA), 3:1 for large text
// and non-text UI components (1.4.11). These checks also cover most color-
// vision deficiencies: contrast ratio is computed from luminance, which is
// what remains discriminable when hue perception is reduced.

const expand = (hex) => {
  const h = hex.replace('#', '');
  return h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
};

export const isHexColor = (value) => typeof value === 'string'
  && /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim());

const channel = (v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4);

export const relativeLuminance = (hex) => {
  const h = expand(hex.trim());
  const [r, g, b] = [0, 2, 4].map((i) => channel(parseInt(h.slice(i, i + 2), 16) / 255));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contrastRatio = (a, b) => {
  if (!isHexColor(a) || !isHexColor(b)) return null;
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

// The combinations the portal actually renders, with the WCAG bar each must
// clear. `fg`/`bg` name keys in a flat theme-palette object (see
// DEFAULT_THEME_PALETTE in theme.js).
const CHECKS = [
  {
    fg: 'text', bg: 'bg', min: 4.5, label: 'Body text on app background',
  },
  {
    fg: 'text', bg: 'surface', min: 4.5, label: 'Body text on cards',
  },
  {
    fg: 'textDim', bg: 'surface', min: 4.5, label: 'Secondary text on cards',
  },
  {
    fg: 'textDim', bg: 'bg', min: 4.5, label: 'Secondary text on app background',
  },
  {
    fg: 'primary', bg: 'surface', min: 3, label: 'Primary accent on cards (buttons, links)',
  },
  {
    fg: 'secondary', bg: 'surface', min: 3, label: 'Secondary accent on cards (badges)',
  },
  {
    fg: 'mapHighlight', bg: 'mapLand', min: 3, label: 'Map: highlighted area vs land',
  },
  {
    fg: 'mapTopoGrid', bg: 'mapLand', min: 3, label: 'Map: tile grid vs land',
  },
  {
    fg: 'mapLandStroke', bg: 'mapLand', min: 3, label: 'Map: county outlines vs land',
  },
  {
    fg: 'mapLand', bg: 'mapBackdrop', min: 1.2, label: 'Map: land vs map backdrop',
  },
];

// Evaluates a flat palette (one mode). Returns one result per check:
// { label, ratio, min, pass }; ratio is null when either color is malformed.
export const evaluatePalette = (palette) => CHECKS.map(({
  fg, bg, min, label,
}) => {
  const ratio = contrastRatio(palette?.[fg], palette?.[bg]);
  return {
    key: `${fg}-${bg}`,
    label,
    min,
    ratio,
    pass: ratio !== null && ratio >= min,
  };
});
